/**
 * Sección «Mis datos» (pantalla Mi salud).
 *
 * Derechos RGPD ejercitables por la propia usuaria, sobre endpoints que ya
 * existían en el backend:
 *   - Acceso y portabilidad (arts. 15 y 20): GET /api/gdpr/me/export → JSON.
 *   - Supresión (art. 17): DELETE /api/gdpr/me → borrado SQL + memoria del
 *     asistente + cuenta Firebase. Tras el borrado el token del cliente deja
 *     de ser válido, por eso se cierra sesión localmente pase lo que pase.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Trash2 } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

const ERROR_SIN_SESION = 'No hemos podido comprobar tu sesión. Recarga la página e inténtalo de nuevo.';
const ERROR_EXPORT = 'No hemos podido preparar la copia de tus datos. Inténtalo de nuevo en unos minutos o escríbenos a info@mirloblancohc.com.';
const ERROR_BORRADO = 'No hemos podido completar el borrado. No se ha eliminado nada. Inténtalo de nuevo o escríbenos a info@mirloblancohc.com.';

function fechaParaFichero(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

export function MisDatosSection() {
  const navigate = useNavigate();
  const { getIdToken, logout } = useAuth();

  const [descargando, setDescargando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [entendido, setEntendido] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const descargar = useCallback(async () => {
    setError(null);
    setAviso(null);
    const token = await getIdToken();
    if (!token) {
      setError(ERROR_SIN_SESION);
      return;
    }
    setDescargando(true);
    try {
      const response = await fetch(`${API_BASE}/api/gdpr/me/export`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`export ${response.status}`);
      const datos = await response.json();
      const blob = new Blob([JSON.stringify(datos, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `maria-mis-datos-${fechaParaFichero()}.json`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
      setAviso('Copia descargada. Guárdala en un lugar seguro: contiene tu información de salud.');
    } catch (e) {
      console.error('Error exportando datos:', e);
      setError(ERROR_EXPORT);
    } finally {
      setDescargando(false);
    }
  }, [getIdToken]);

  const eliminar = useCallback(async () => {
    setError(null);
    setAviso(null);
    const token = await getIdToken();
    if (!token) {
      setError(ERROR_SIN_SESION);
      return;
    }
    setBorrando(true);
    try {
      const response = await fetch(`${API_BASE}/api/gdpr/me`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`delete ${response.status}`);
    } catch (e) {
      console.error('Error eliminando datos:', e);
      setError(ERROR_BORRADO);
      setBorrando(false);
      return;
    }
    // El backend ya ha borrado la cuenta Firebase: el token local es inválido.
    // logout() tolera el fallo de signOut y limpia el estado de la app.
    await logout();
    navigate('/', { replace: true });
  }, [getIdToken, logout, navigate]);

  const cancelar = () => {
    setConfirmando(false);
    setEntendido(false);
  };

  return (
    <section
      aria-labelledby="mis-datos-titulo"
      className="max-w-4xl mx-auto px-4 pb-10"
    >
      <div className="rounded-lg border border-border bg-card p-6 space-y-4">
        <div>
          <h2 id="mis-datos-titulo" className="text-lg font-semibold text-foreground">
            Mis datos
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Tus datos son tuyos. Aquí puedes llevarte una copia o borrarlo todo, sin pedírnoslo
            y sin esperar.
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {aviso && (
          <Alert>
            <AlertDescription>{aviso}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={descargar}
            disabled={descargando || borrando}
          >
            <Download className="h-4 w-4 mr-2" aria-hidden="true" />
            {descargando ? 'Preparando copia...' : 'Descargar mis datos'}
          </Button>

          {!confirmando && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => setConfirmando(true)}
              disabled={descargando || borrando}
            >
              <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
              Eliminar mis datos
            </Button>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          La copia es un fichero JSON con tu encuesta, tus conversaciones y la memoria del
          asistente. Si prefieres hacerlo por escrito, escríbenos a{' '}
          <a href="mailto:info@mirloblancohc.com" className="text-primary hover:underline">
            info@mirloblancohc.com
          </a>
          .
        </p>

        {confirmando && (
          <div
            role="dialog"
            aria-modal="false"
            aria-labelledby="confirmar-borrado-titulo"
            className="rounded-md border border-red-300 bg-red-50 p-4 space-y-3"
          >
            <h3 id="confirmar-borrado-titulo" className="font-semibold text-red-800">
              ¿Seguro que quieres eliminar todos tus datos?
            </h3>
            <p className="text-sm text-red-900">
              Esta acción es irreversible. Se borran ahora mismo tu encuesta de salud, todas tus
              conversaciones, la memoria del asistente y tu cuenta de acceso. No podremos
              recuperarlos después. Si quieres conservar una copia, descárgala antes.
            </p>
            <label className="flex items-start gap-2 text-sm text-red-900">
              <input
                type="checkbox"
                className="mt-1"
                checked={entendido}
                onChange={(e) => setEntendido(e.target.checked)}
                disabled={borrando}
              />
              <span>Entiendo que es irreversible y quiero eliminar todos mis datos.</span>
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant="destructive"
                onClick={eliminar}
                disabled={!entendido || borrando}
              >
                {borrando ? 'Eliminando...' : 'Eliminar definitivamente'}
              </Button>
              <Button type="button" variant="ghost" onClick={cancelar} disabled={borrando}>
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
